// The playback indicator — the one place the dashboard says "this is a
// replay, and here is the RECORDING time".
//
// A boat on a sim-time clock uploads data carrying a recording's own
// timestamps. Without this banner that state is indistinguishable from a
// broken boat; with it, the fast-moving gauges and the historical dates all
// read as what they are. Rendered by BoatShell above the route content (the
// BoatUpdateBanner slot), so it persists on every boat page.
//
// The recording clock ticks per second WITHOUT polling: the reported pair
// (virtualNowIso, reportedAtIso) is linear in wall time, so each render
// extrapolates from the shell's existing 1 s `now` tick.

import {
  episodeClockMs,
  formatRecordingClock,
  playbackNowMs,
  type SimTimeStatus,
} from '../../lib/playback';

export interface PlaybackBannerProps {
  /** The boat's reported playback clock; render nothing when null. */
  sim: SimTimeStatus | null | undefined;
  /** The shell's ticking wall clock (1 s cadence). */
  now: number;
}

export function PlaybackBanner({ sim, now }: PlaybackBannerProps): React.ReactElement | null {
  if (!sim) return null;
  // Looped replay: fold the advancing virtual clock into the episode.
  const virtualNow = sim.spanMs != null ? episodeClockMs(sim, now) : playbackNowMs(sim, now);
  const recordedDay = sim.anchorIso.slice(0, 10);
  const wallDay = new Date(now).toISOString().slice(0, 10);
  return (
    <div className="playback-banner" role="status" data-testid="playback-banner">
      <span className="playback-banner-head">
        Playback · {sim.rate}×{sim.loop != null ? ` · loop ${sim.loop}` : ''}
      </span>
      <span className="playback-banner-clock">{formatRecordingClock(virtualNow)}</span>
      {recordedDay !== wallDay ? (
        <span className="playback-banner-note">replaying a recording from {recordedDay}</span>
      ) : null}
    </div>
  );
}
